import React from 'react';
import { motion } from 'motion/react';
import { alpha } from '@mui/material';
import { useMotionSafe } from '../../../motion';
import { glory, grey } from '../../../theme/tokens';

/**
 * Two meshing spur gears as a technical drawing: tooth outlines, dashed pitch
 * circles, dash-dot centrelines and a protractor ring. The gears turn at their
 * true 2:1 ratio, so the teeth stay in mesh; under reduced motion they hold
 * still. Decorative only.
 */

const BIG = { teeth: 28, outer: 210, root: 188 };
const SMALL = { teeth: 14, outer: 109, root: 87 };
/** Pitch radii — where the gears meet. */
const BIG_PITCH = (BIG.outer + BIG.root) / 2;
const SMALL_PITCH = (SMALL.outer + SMALL.root) / 2;
/** The small gear sits down and to the left of the big one. */
const MESH_ANGLE = 140;
const TURN_SECONDS = 90;

const rad = (deg: number) => (deg * Math.PI) / 180;
const point = (r: number, a: number) => `${(r * Math.cos(a)).toFixed(2)} ${(r * Math.sin(a)).toFixed(2)}`;

/** Outline of a spur gear centred on the origin: flat-topped teeth, arc roots. */
function gearOutline(teeth: number, outer: number, root: number): string {
  const step = (2 * Math.PI) / teeth;
  let d = `M ${point(root, step * 0.12)}`;
  for (let i = 0; i < teeth; i++) {
    const a = i * step;
    d += ` L ${point(outer, a + step * 0.32)}`;
    d += ` A ${outer} ${outer} 0 0 1 ${point(outer, a + step * 0.68)}`;
    d += ` L ${point(root, a + step * 0.88)}`;
    d += ` A ${root} ${root} 0 0 1 ${point(root, a + step * 1.12)}`;
  }
  return `${d} Z`;
}

const BIG_PATH = gearOutline(BIG.teeth, BIG.outer, BIG.root);
const SMALL_PATH = gearOutline(SMALL.teeth, SMALL.outer, SMALL.root);

const Gear: React.FC<{
  path: string;
  outer: number;
  pitch: number;
  spokes: number;
  stroke: string;
  start: number;
  turns: number;
}> = ({ path, outer, pitch, spokes, stroke, start, turns }) => {
  const { enabled } = useMotionSafe();
  const body = (
    <>
      <path d={path} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinejoin="round" />
      <circle r={pitch} fill="none" stroke={stroke} strokeWidth={0.75} strokeDasharray="6 5" opacity={0.6} />
      <circle r={outer * 0.34} fill="none" stroke={stroke} strokeWidth={1.25} />
      <circle r={outer * 0.15} fill="none" stroke={stroke} strokeWidth={1.25} />
      {Array.from({ length: spokes }, (_, i) => {
        const a = (2 * Math.PI * i) / spokes;
        return (
          <circle
            key={i}
            cx={outer * 0.6 * Math.cos(a)}
            cy={outer * 0.6 * Math.sin(a)}
            r={outer * 0.13}
            fill="none"
            stroke={stroke}
            strokeWidth={1.25}
          />
        );
      })}
    </>
  );

  if (!enabled) return <g transform={`rotate(${start})`}>{body}</g>;
  return (
    <motion.g
      initial={{ rotate: start }}
      animate={{ rotate: start + 360 * turns }}
      transition={{ duration: TURN_SECONDS, repeat: Infinity, ease: 'linear' }}
    >
      {body}
    </motion.g>
  );
};

const GearDrawing: React.FC<{ size?: number }> = ({ size = 680 }) => {
  const line = alpha(grey[50], 0.2);
  const faint = alpha(grey[50], 0.1);
  const red = alpha(glory[400], 0.75);

  const mesh = rad(MESH_ANGLE);
  const distance = BIG_PITCH + SMALL_PITCH;
  const smallX = distance * Math.cos(mesh);
  const smallY = distance * Math.sin(mesh);

  // Put a gap of the big gear (gaps sit at whole tooth steps) and a tooth of
  // the small one (teeth sit at half steps) on the line between their centres,
  // so they start, and stay, in mesh.
  const bigStart = MESH_ANGLE;
  const smallStart = MESH_ANGLE + 180 - 360 / SMALL.teeth / 2;

  const ticks = Array.from({ length: 72 }, (_, i) => {
    const a = rad(i * 5);
    const long = i % 6 === 0;
    const r1 = 236;
    const r2 = long ? 252 : 244;
    return (
      <line
        key={i}
        x1={r1 * Math.cos(a)}
        y1={r1 * Math.sin(a)}
        x2={r2 * Math.cos(a)}
        y2={r2 * Math.sin(a)}
        stroke={long ? line : faint}
        strokeWidth={long ? 1.25 : 1}
      />
    );
  });

  return (
    <svg
      width={size}
      height={size}
      viewBox="-420 -300 720 720"
      aria-hidden
      focusable="false"
      style={{ display: 'block', overflow: 'visible' }}
    >
      {/* Protractor ring and centrelines stay put while the gears turn. */}
      <circle r={236} fill="none" stroke={faint} strokeWidth={1} />
      {ticks}
      <line x1={-280} y1={0} x2={280} y2={0} stroke={faint} strokeWidth={1} strokeDasharray="18 6 3 6" />
      <line x1={0} y1={-280} x2={0} y2={280} stroke={faint} strokeWidth={1} strokeDasharray="18 6 3 6" />
      <line
        x1={smallX - 150}
        y1={smallY}
        x2={smallX + 150}
        y2={smallY}
        stroke={faint}
        strokeWidth={1}
        strokeDasharray="18 6 3 6"
      />
      <line
        x1={smallX}
        y1={smallY - 150}
        x2={smallX}
        y2={smallY + 150}
        stroke={faint}
        strokeWidth={1}
        strokeDasharray="18 6 3 6"
      />

      <Gear path={BIG_PATH} outer={BIG.outer} pitch={BIG_PITCH} spokes={6} stroke={line} start={bigStart} turns={1} />
      <g transform={`translate(${smallX.toFixed(2)} ${smallY.toFixed(2)})`}>
        <Gear
          path={SMALL_PATH}
          outer={SMALL.outer}
          pitch={SMALL_PITCH}
          spokes={5}
          stroke={red}
          start={smallStart}
          turns={-BIG.teeth / SMALL.teeth}
        />
        <circle r={4} fill={glory[400]} />
      </g>
      <circle r={4} fill={line} />
    </svg>
  );
};

export default GearDrawing;
